import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Image as ImageIcon, Eye, EyeOff } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface SteganographyStats {
  totalOperations: number;
  messagesHidden: number;
  messagesExtracted: number;
  totalImagesProcessed: number;
  totalBytesHidden: number;
  averageMessageLength: number;
  longestMessage: number;
  compressionRatio: number;
}

interface SteganographyHistory {
  id: string;
  operation: 'hide' | 'extract';
  message: string;
  filename: string;
  messageLength: number;
  timestamp: Date;
  success: boolean;
}

const ImageSteganographyApp: React.FC = () => {
  // Core state
  const [secretMessage, setSecretMessage] = useState<string>('');
  const [extractedMessage, setExtractedMessage] = useState<string>('');
  const [operation, setOperation] = useState<'hide' | 'extract'>('hide');
  const [password, setPassword] = useState<string>('');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'preview' | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  // Image state
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(null);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [imageCapacity, setImageCapacity] = useState<number>(0);
  
  // Advanced options state
  const [useEncryption, setUseEncryption] = useState<boolean>(false);
  const [bitsPerChannel, setBitsPerChannel] = useState<number>(1);
  const [compressionLevel, setCompressionLevel] = useState<number>(0);
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [preserveQuality, setPreserveQuality] = useState<boolean>(true);
  const [randomizePattern, setRandomizePattern] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<SteganographyStats>({
    totalOperations: 0,
    messagesHidden: 0,
    messagesExtracted: 0,
    totalImagesProcessed: 0,
    totalBytesHidden: 0,
    averageMessageLength: 0,
    longestMessage: 0,
    compressionRatio: 0
  });
  const [history, setHistory] = useState<SteganographyHistory[]>([]);
  
  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null);

  // Simple XOR encryption for password protection
  const encryptMessage = useCallback((message: string, key: string): string => {
    if (!key) return message;
    let encrypted = '';
    for (let i = 0; i < message.length; i++) {
      encrypted += String.fromCharCode(message.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return btoa(encrypted); // Base64 encode
  }, []);

  const decryptMessage = useCallback((encrypted: string, key: string): string => {
    if (!key) return encrypted;
    try {
      const decoded = atob(encrypted); // Base64 decode
      let decrypted = '';
      for (let i = 0; i < decoded.length; i++) {
        decrypted += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return decrypted;
    } catch {
      return '';
    }
  }, []);

  // Calculate image capacity for hiding data
  const calculateCapacity = useCallback((width: number, height: number): number => {
    // Each pixel has 3 channels (RGB), each can hide bitsPerChannel bits
    const totalPixels = width * height;
    const bitsAvailable = totalPixels * 3 * bitsPerChannel;
    const bytesAvailable = Math.floor(bitsAvailable / 8);
    // Reserve some bytes for metadata (message length, etc.)
    return Math.max(0, bytesAvailable - 32);
  }, [bitsPerChannel]);

  // Convert string to binary
  const stringToBinary = useCallback((str: string): string => {
    return str.split('').map(char => 
      char.charCodeAt(0).toString(2).padStart(8, '0')
    ).join('');
  }, []);

  // Convert binary to string
  const binaryToString = useCallback((binary: string): string => {
    const result = [];
    for (let i = 0; i < binary.length; i += 8) {
      const byte = binary.slice(i, i + 8);
      if (byte.length === 8) {
        result.push(String.fromCharCode(parseInt(byte, 2)));
      }
    }
    return result.join('');
  }, []);

  // Hide message in image using LSB steganography
  const hideMessage = useCallback(async (image: HTMLImageElement, message: string): Promise<string | null> => {
    if (!canvasRef.current) return null;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Prepare message with metadata
    const processedMessage = useEncryption ? encryptMessage(message, password) : message;
    const messageWithLength = message.length.toString().padStart(16, '0') + processedMessage;
    const binaryMessage = stringToBinary(messageWithLength);

    // Check capacity
    const capacity = calculateCapacity(canvas.width, canvas.height);
    if (processedMessage.length > capacity) {
      throw new Error(`Message too long. Maximum capacity: ${capacity} characters`);
    }

    // Hide message in LSBs
    let messageIndex = 0;
    const mask = (1 << bitsPerChannel) - 1; // Create mask for LSBs

    for (let i = 0; i < data.length && messageIndex < binaryMessage.length; i += 4) {
      // Skip alpha channel, work with RGB
      for (let channel = 0; channel < 3 && messageIndex < binaryMessage.length; channel++) {
        const pixelIndex = i + channel;
        const originalValue = data[pixelIndex];
        
        // Extract bits to hide
        const bitsToHide = binaryMessage.slice(messageIndex, messageIndex + bitsPerChannel);
        messageIndex += bitsPerChannel;
        
        if (bitsToHide.length > 0) {
          // Clear LSBs and set new bits
          const newValue = (originalValue & ~mask) | parseInt(bitsToHide.padEnd(bitsPerChannel, '0'), 2);
          data[pixelIndex] = newValue;
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  }, [canvasRef, useEncryption, encryptMessage, password, stringToBinary, calculateCapacity, bitsPerChannel]);

  // Extract message from image
  const extractMessage = useCallback(async (image: HTMLImageElement): Promise<string> => {
    if (!canvasRef.current) return '';
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let binaryMessage = '';
    const mask = (1 << bitsPerChannel) - 1;

    // Extract message length first (16 characters = 128 bits)
    let bitsExtracted = 0;
    const lengthBits = 128; // 16 characters * 8 bits

    for (let i = 0; i < data.length && bitsExtracted < lengthBits; i += 4) {
      for (let channel = 0; channel < 3 && bitsExtracted < lengthBits; channel++) {
        const pixelIndex = i + channel;
        const value = data[pixelIndex];
        const extractedBits = (value & mask).toString(2).padStart(bitsPerChannel, '0');
        binaryMessage += extractedBits;
        bitsExtracted += bitsPerChannel;
      }
    }

    // Get message length
    const lengthString = binaryToString(binaryMessage.slice(0, lengthBits));
    const messageLength = parseInt(lengthString);
    
    if (isNaN(messageLength) || messageLength <= 0) {
      throw new Error('No valid message found in image');
    }

    // Extract the actual message
    const totalBitsNeeded = lengthBits + (messageLength * 8);
    
    for (let i = Math.floor(lengthBits / (3 * bitsPerChannel)) * 4; i < data.length && bitsExtracted < totalBitsNeeded; i += 4) {
      for (let channel = 0; channel < 3 && bitsExtracted < totalBitsNeeded; channel++) {
        const pixelIndex = i + channel;
        const value = data[pixelIndex];
        const extractedBits = (value & mask).toString(2).padStart(bitsPerChannel, '0');
        binaryMessage += extractedBits;
        bitsExtracted += bitsPerChannel;
      }
    }

    // Convert binary to string and extract message
    const fullMessage = binaryToString(binaryMessage.slice(lengthBits, totalBitsNeeded));
    
    if (useEncryption) {
      const decrypted = decryptMessage(fullMessage, password);
      if (!decrypted && password) {
        throw new Error('Failed to decrypt message. Check password.');
      }
      return decrypted;
    }
    
    return fullMessage;
  }, [canvasRef, bitsPerChannel, binaryToString, useEncryption, decryptMessage, password]);

  // Handle image upload
  const handleImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = document.createElement('img') as HTMLImageElement;
      img.onload = () => {
        setOriginalImage(img);
        const capacity = calculateCapacity(img.width, img.height);
        setImageCapacity(capacity);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }, [calculateCapacity]);

  // Process steganography operation
  const processOperation = useCallback(async () => {
    if (!originalImage) return;

    setIsProcessing(true);
    try {
      if (operation === 'hide') {
        if (!secretMessage.trim()) {
          throw new Error('Please enter a message to hide');
        }
        const result = await hideMessage(originalImage, secretMessage);
        if (result) {
          setProcessedImage(result);
        }
      } else {
        const result = await extractMessage(originalImage);
        setExtractedMessage(result);
      }

      // Update analytics
      updateAnalytics();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  }, [originalImage, operation, secretMessage, hideMessage, extractMessage]);

  // Update analytics
  const updateAnalytics = useCallback(() => {
    const newHistoryItem: SteganographyHistory = {
      id: Date.now().toString(),
      operation,
      message: operation === 'hide' ? secretMessage : extractedMessage,
      filename: 'image.png',
      messageLength: operation === 'hide' ? secretMessage.length : extractedMessage.length,
      timestamp: new Date(),
      success: true
    };

    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);

    setStats(prevStats => {
      const totalOps = prevStats.totalOperations + 1;
      const totalBytes = prevStats.totalBytesHidden + (operation === 'hide' ? secretMessage.length : 0);
      
      return {
        ...prevStats,
        totalOperations: totalOps,
        messagesHidden: prevStats.messagesHidden + (operation === 'hide' ? 1 : 0),
        messagesExtracted: prevStats.messagesExtracted + (operation === 'extract' ? 1 : 0),
        totalImagesProcessed: prevStats.totalImagesProcessed + 1,
        totalBytesHidden: totalBytes,
        averageMessageLength: Math.round(totalBytes / Math.max(prevStats.messagesHidden + (operation === 'hide' ? 1 : 0), 1)),
        longestMessage: Math.max(prevStats.longestMessage, secretMessage.length),
        compressionRatio: compressionLevel
      };
    });
  }, [operation, secretMessage, extractedMessage, compressionLevel]);

  // Auto-calculate capacity when image or settings change
  useEffect(() => {
    if (originalImage) {
      const capacity = calculateCapacity(originalImage.width, originalImage.height);
      setImageCapacity(capacity);
    }
  }, [originalImage, bitsPerChannel, calculateCapacity]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadImage = () => {
    if (processedImage) {
      const link = document.createElement('a');
      link.download = 'steganography-image.png';
      link.href = processedImage;
      link.click();
    }
  };

  const resetAll = () => {
    setSecretMessage('');
    setExtractedMessage('');
    setOriginalImage(null);
    setProcessedImage(null);
    setPassword('');
    setOperation('hide');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const generateSampleMessage = () => {
    const samples = [
      "This is a secret message hidden in the image!",
      "Steganography allows hiding data in plain sight.",
      "The quick brown fox jumps over the lazy dog.",
      "Hidden messages can contain sensitive information.",
      "Digital steganography is a powerful security technique.",
      "LSB steganography modifies the least significant bits.",
      "This message is invisible to the naked eye."
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setSecretMessage(randomSample);
  };

  return (
    <>
      <Helmet>
        <title>Image Steganography - Hide & Extract Secret Messages in Images | Free Online Tool</title>
        <meta name="description" content="Professional image steganography tool to hide and extract secret messages in images using LSB techniques. Secure, free, and easy to use with encryption support." />
        <meta name="keywords" content="image steganography, hide message in image, LSB steganography, secret message, digital steganography, image encryption, data hiding, steganography tool, image security" />
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <ImageIcon className="text-indigo-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Image Steganography</h1>
                <p className="text-sm opacity-60">Hide & Extract Secret Messages in Images</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setActiveModal(activeModal === 'preview' ? null : 'preview')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Image Preview"
              >
                {showPreview ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'hide' | 'extract')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="hide">🔒 Hide Message</option>
                    <option value="extract">🔍 Extract Message</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Upload Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className={`w-full p-2 rounded border text-sm ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Password (Optional)</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={processOperation}
                    disabled={!originalImage || isProcessing}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      !originalImage || isProcessing
                        ? 'bg-gray-400 cursor-not-allowed'
                        : isDarkMode 
                        ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' 
                        : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                    } transition-colors`}
                  >
                    {isProcessing ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    ) : operation === 'hide' ? (
                      <>
                        <Shield size={16} />
                        <span>Hide</span>
                      </>
                    ) : (
                      <>
                        <Eye size={16} />
                        <span>Extract</span>
                      </>
                    )}
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleMessage}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample message"
                  >
                    <Shuffle size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>

              {/* Image Info */}
              {originalImage && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Image Information:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="font-medium mb-1">Dimensions</div>
                      <div className="opacity-60">{originalImage.width} × {originalImage.height}</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1">Capacity</div>
                      <div className="opacity-60">{imageCapacity.toLocaleString()} characters</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1">Bits per Channel</div>
                      <div className="opacity-60">{bitsPerChannel} bit{bitsPerChannel > 1 ? 's' : ''}</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1">Encryption</div>
                      <div className="opacity-60">{useEncryption ? 'Enabled' : 'Disabled'}</div>
                    </div>
                  </div>
                  
                  {operation === 'hide' && secretMessage && (
                    <div className="mt-3">
                      <div className="text-xs font-medium mb-2">Message Status:</div>
                      <div className={`text-xs px-2 py-1 rounded ${
                        secretMessage.length <= imageCapacity
                          ? 'bg-green-100 text-green-800 border border-green-300'
                          : 'bg-red-100 text-red-800 border border-red-300'
                      }`}>
                        {secretMessage.length} / {imageCapacity} characters
                        {secretMessage.length > imageCapacity && ' (Message too long!)'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Message Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Hide Message */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Secret Message {operation === 'hide' ? '(Input)' : '(Output)'}</h3>
                  <div className="text-sm opacity-60">
                    {operation === 'hide' ? secretMessage.length : extractedMessage.length} characters
                  </div>
                </div>
                <textarea
                  value={operation === 'hide' ? secretMessage : extractedMessage}
                  onChange={(e) => operation === 'hide' && setSecretMessage(e.target.value)}
                  placeholder={operation === 'hide' ? "Enter your secret message to hide in the image..." : "Extracted message will appear here..."}
                  readOnly={operation === 'extract'}
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-xs opacity-60">
                    {operation === 'hide' ? 'Message to hide' : 'Extracted message'}
                  </div>
                  {((operation === 'hide' && secretMessage) || (operation === 'extract' && extractedMessage)) && (
                    <button
                      onClick={() => copyToClipboard(operation === 'hide' ? secretMessage : extractedMessage)}
                      className={`px-3 py-1 text-xs rounded ${
                        isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
                      } text-white transition-colors`}
                    >
                      <Copy size={12} className="inline mr-1" />
                      Copy
                    </button>
                  )}
                </div>
              </div>

              {/* Image Preview */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Image Preview</h3>
                  {processedImage && (
                    <button
                      onClick={downloadImage}
                      className={`px-3 py-1 text-xs rounded flex items-center space-x-1 ${
                        isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                      } text-white transition-colors`}
                    >
                      <Download size={12} />
                      <span>Download</span>
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  {originalImage && (
                    <div>
                      <div className="text-sm font-medium mb-2">Original Image:</div>
                      <div className="border rounded overflow-hidden">
                        <img
                          src={originalImage.src}
                          alt="Original"
                          className="w-full h-48 object-contain bg-gray-100"
                        />
                      </div>
                    </div>
                  )}
                  
                  {processedImage && operation === 'hide' && (
                    <div>
                      <div className="text-sm font-medium mb-2">Image with Hidden Message:</div>
                      <div className="border rounded overflow-hidden">
                        <img
                          src={processedImage}
                          alt="Processed"
                          className="w-full h-48 object-contain bg-gray-100"
                        />
                      </div>
                    </div>
                  )}
                  
                  {!originalImage && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-gray-500">Upload an image to get started</p>
                      <p className="text-xs text-gray-400 mt-2">Supports PNG, JPG, GIF formats</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Hidden Canvas */}
        <canvas ref={canvasRef} className="hidden" />
        <canvas ref={hiddenCanvasRef} className="hidden" />

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-indigo-500" />
                  <span>About Image Steganography</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is Image Steganography?</h3>
                  <p>Image steganography is the practice of hiding secret information within digital images in such a way that the existence of the hidden data is not detectable to the human eye. Unlike encryption, which scrambles data to make it unreadable, steganography conceals the very existence of the secret message.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How LSB Steganography Works</h3>
                  <p>This tool uses LSB (Least Significant Bit) steganography, which works by modifying the least significant bits of pixel color values. Since these tiny changes are imperceptible to human vision, the image appears unchanged while secretly containing hidden data.</p>
                  <ol className="list-decimal list-inside mt-2 space-y-1">
                    <li>Convert your secret message to binary format</li>
                    <li>Replace the least significant bits of pixel RGB values</li>
                    <li>The modified image looks identical to the original</li>
                    <li>Extract the message by reading the LSBs back</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">LSB Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="space-y-2 text-xs font-mono">
                      <div><strong>Original pixel RGB:</strong> (255, 128, 64)</div>
                      <div><strong>Binary:</strong> (11111111, 10000000, 01000000)</div>
                      <div><strong>Message bit to hide:</strong> 1</div>
                      <div><strong>Modified LSB:</strong> (11111111, 10000000, 01000001)</div>
                      <div><strong>New RGB:</strong> (255, 128, 65)</div>
                      <div className="text-gray-500">Change is invisible to human eye!</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Features</h3>
                  <ul className="space-y-1">
                    <li><strong>LSB Steganography:</strong> Industry-standard technique</li>
                    <li><strong>Password Protection:</strong> Optional XOR encryption</li>
                    <li><strong>Multiple Formats:</strong> PNG, JPG, GIF support</li>
                    <li><strong>Adjustable Bits:</strong> 1-3 bits per color channel</li>
                    <li><strong>Capacity Calculation:</strong> Real-time capacity checking</li>
                    <li><strong>Quality Preservation:</strong> Minimal visual impact</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Features</h3>
                  <ul className="space-y-1">
                    <li><strong>Encryption:</strong> XOR cipher with password</li>
                    <li><strong>Invisible Changes:</strong> Modifications undetectable to eye</li>
                    <li><strong>No File Size Change:</strong> Maintains original file size</li>
                    <li><strong>Format Preservation:</strong> Works with common image formats</li>
                    <li><strong>Metadata Protection:</strong> Message length encoding</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Applications</h3>
                  <ul className="space-y-1">
                    <li>Secure communication and messaging</li>
                    <li>Digital watermarking and copyright protection</li>
                    <li>Covert data transmission</li>
                    <li>Authentication and integrity verification</li>
                    <li>Educational cryptography demonstrations</li>
                    <li>Privacy protection for sensitive information</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Best Practices</h3>
                  <ul className="space-y-1">
                    <li>Use high-resolution images for better capacity</li>
                    <li>Choose images with complex textures and colors</li>
                    <li>Avoid images that will be compressed or resized</li>
                    <li>Use strong passwords for sensitive messages</li>
                    <li>Test extraction before sharing hidden images</li>
                    <li>Keep message length within capacity limits</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Limitations</h3>
                  <ul className="space-y-1">
                    <li>Vulnerable to statistical analysis</li>
                    <li>Limited by image capacity</li>
                    <li>Sensitive to image compression and editing</li>
                    <li>Not suitable for large data files</li>
                    <li>Detection possible with specialized software</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Context</h3>
                  <p>Steganography has ancient roots, from invisible inks to modern digital techniques. The term comes from Greek words meaning "covered writing." Digital steganography emerged with computer graphics, providing new ways to hide information in multimedia files.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Warning</h3>
                  <p className="text-orange-600">⚠️ <strong>Educational Use:</strong> This tool is designed for educational purposes and basic privacy needs. For high-security applications, use professional-grade steganography tools combined with strong encryption methods.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Use Encryption</label>
                  <input
                    type="checkbox"
                    checked={useEncryption}
                    onChange={(e) => setUseEncryption(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Preview</label>
                  <input
                    type="checkbox"
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Quality</label>
                  <input
                    type="checkbox"
                    checked={preserveQuality}
                    onChange={(e) => setPreserveQuality(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Bits per Channel</label>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    value={bitsPerChannel}
                    onChange={(e) => setBitsPerChannel(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs opacity-60 mt-1">
                    {bitsPerChannel} bit{bitsPerChannel > 1 ? 's' : ''} per color channel
                  </div>
                  <div className="text-xs opacity-60">
                    Higher values = more capacity, lower quality
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Compression Level</label>
                  <input
                    type="range"
                    min="0"
                    max="9"
                    value={compressionLevel}
                    onChange={(e) => setCompressionLevel(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs opacity-60 mt-1">
                    Level {compressionLevel} - {compressionLevel === 0 ? 'No compression' : compressionLevel < 4 ? 'Low compression' : compressionLevel < 7 ? 'Medium compression' : 'High compression'}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Quick Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setBitsPerChannel(1);
                        setUseEncryption(true);
                        setPreserveQuality(true);
                        setCompressionLevel(0);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                      } transition-colors`}
                    >
                      High Security
                    </button>
                    
                    <button
                      onClick={() => {
                        setBitsPerChannel(2);
                        setUseEncryption(false);
                        setPreserveQuality(true);
                        setCompressionLevel(3);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Balanced
                    </button>
                    
                    <button
                      onClick={() => {
                        setBitsPerChannel(3);
                        setUseEncryption(false);
                        setPreserveQuality(false);
                        setCompressionLevel(6);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Maximum Capacity
                    </button>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Information</h3>
                  <div className="text-xs space-y-2 opacity-60">
                    <div>• LSB steganography modifies pixel color values</div>
                    <div>• 1 bit per channel provides best invisibility</div>
                    <div>• Encryption protects against casual inspection</div>
                    <div>• Use PNG format for best quality preservation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.messagesHidden}</div>
                  <div className="text-sm">Messages Hidden</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.messagesExtracted}</div>
                  <div className="text-sm">Messages Extracted</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.totalImagesProcessed}</div>
                  <div className="text-sm">Images Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalBytesHidden.toLocaleString()}</div>
                  <div className="text-sm">Bytes Hidden</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.averageMessageLength}</div>
                  <div className="text-sm">Avg Message Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.longestMessage}</div>
                  <div className="text-sm">Longest Message</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.totalOperations}</div>
                  <div className="text-sm">Total Operations</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.compressionRatio}</div>
                  <div className="text-sm">Compression Level</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'hide' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation === 'hide' ? 'HIDE' : 'EXTRACT'}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.messageLength} chars
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.success ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                          }`}>
                            {item.success ? 'SUCCESS' : 'FAILED'}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono text-gray-600">{item.filename}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.message.slice(0, 50)}{item.message.length > 50 ? '...' : ''}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <ImageIcon size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start hiding or extracting messages to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Steganography Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Success Rate:</div>
                      <div>
                        {history.length > 0 
                          ? Math.round((history.filter(h => h.success).length / history.length) * 100)
                          : 0
                        }% Success
                      </div>
                    </div>
                    <div>
                      <div className="text-green-500">Hide/Extract Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.messagesHidden / stats.totalOperations) * 100) : 0}% Hide</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Data Efficiency:</div>
                      <div>{stats.totalImagesProcessed > 0 ? Math.round(stats.totalBytesHidden / stats.totalImagesProcessed) : 0} bytes/image</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Steganography Efficiency:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.messagesHidden / stats.totalOperations) * 100) : 0}% Hidden</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ImageSteganographyApp;